import type { FC } from 'react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, List, Popover, Spin, Typography, message } from 'antd';
import { theme } from 'antd';
import moment from 'moment';
import { CloseOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { ReactComponent as NoticeSvg } from '../../assets/header/notice.svg';
import { ReactComponent as NotificationActive } from '../../assets/logo/notficationActive.svg';
import { ReactComponent as NotificationInActive } from '../../assets/logo/notificationInactive.svg';
import { ReactComponent as DoubleCheck } from '../../assets/logo/doubleCheck.svg';
import {
  useMarkAllAsRead,
  useMarkAsRead,
  useNotifications,
  useUnreadCount,
} from '../../../Hooks/Notifications';

const { Text, Paragraph } = Typography;

interface AdminNotificationProps {
  color: string | undefined;
}

const AdminNotification: FC<AdminNotificationProps> = ({ color }) => {
  const { useToken } = theme;
  const { token } = useToken();
  const navigate = useNavigate();

  const [visible, setVisible] = useState<boolean>(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const { data: notifications = [], isLoading, refetch } = useNotifications();
  const { data: unreadCount = 0, refetch: refetchUnreadCount } = useUnreadCount();
  const { mutate: markAllAsReadMutation, isLoading: isMarkingAll } = useMarkAllAsRead();
  const { mutate: markAsReadMutation, isLoading: isMarking } = useMarkAsRead();

  const groupByDate = notifications.reduce<Record<string, typeof notifications>>((acc, item) => {
    const date = moment(item.createdAt).format('ddd MMM DD YYYY');
    if (!acc[date]) acc[date] = [];
    acc[date].push(item);
    return acc;
  }, {});

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation(undefined, {
      onSuccess: () => {
        message.success('All notifications marked as read');
        refetch();
        refetchUnreadCount();
      },
      onError: () => {
        message.error('Failed to mark all as read');
      },
    });
  };

  const handleNotificationClick = (id: string) => {
    markAsReadMutation(id, {
      onSuccess: (res) => {
        const notification = res.data.data.notification;

        let targetPath = '/';
        if (notification.title.includes('FMCSA')) {
          targetPath = '/fmcsa';
          navigate(targetPath);
        } else if (notification.title.includes('User registered')) {
          targetPath = '/';
          navigate(targetPath);
        }

        setVisible(false);

        message.success('Notification marked as read');
        refetch();
        refetchUnreadCount();
      },
      onError: () => {
        message.error('Failed to mark as read');
      },
    });
  };

  const toggleDescription = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const content = (
    <div
      style={{
        height: 500,
        overflowY: 'auto',
        paddingRight: 8,
        width: 380,
      }}
    >
      <Spin spinning={isLoading}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <Text strong style={{ fontSize: 16 }}>
            Notifications
          </Text>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="default"
              icon={<DoubleCheck />}
              onClick={handleMarkAllAsRead}
              disabled={isLoading || isMarkingAll}
            >
              Mark all as read
            </Button>
            <Button type="default" onClick={() => setVisible(false)} icon={<CloseOutlined />} />
          </div>
        </div>

        {Object.keys(groupByDate).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>No notifications</div>
        ) : (
          Object.entries(groupByDate).map(([date, items]) => {
            return (
              <div key={date}>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 12,
                    padding: '6px 0',
                    borderTop: `1px solid ${token.colorSplit}`,
                    borderBottom: `1px solid ${token.colorSplit}`,
                    width: '100%',
                    display: 'block',
                    margin: '12px 0',
                  }}
                >
                  {date}
                </Text>

                <List
                  dataSource={items}
                  itemLayout="vertical"
                  renderItem={(item) => {
                    const isExpanded = expandedIds.has(item.id);

                    return (
                      <List.Item
                        key={item.id}
                        style={{
                          backgroundColor: item.read ? token.colorBgLayout : token.colorBgContainer,
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          padding: '12px 16px',
                          border: `1px solid ${token.colorBorderSecondary}`,
                          boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                          marginBottom: 12,
                          transition: 'all 0.3s',
                          cursor: 'default',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            width: '100%',
                            gap: 8,
                          }}
                        >
                          <div style={{ flexShrink: 0 }}>
                            {item.read ? <NotificationInActive /> : <NotificationActive />}
                          </div>

                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              flex: 1,
                              overflow: 'hidden',
                            }}
                          >
                            <Text strong style={{ width: '100%' }}>
                              {item.title === 'User registered'
                                ? `New User Registration Request from ${item.user?.additionalData?.company_name}`
                                : item.title}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12, marginTop: 2 }}>
                              {moment(item.createdAt).fromNow()},{' '}
                              {moment(item.createdAt).format('hh:mm A')}
                            </Text>
                          </div>

                          <div
                            onClick={(e) => toggleDescription(item.id, e)}
                            style={{
                              marginLeft: 'auto',
                              cursor: 'pointer',
                              flexShrink: 0,
                              color: token.colorTextSecondary,
                            }}
                          >
                            {isExpanded ? <UpOutlined /> : <DownOutlined />}
                          </div>
                        </div>

                        {isExpanded && (
                          <div style={{ marginTop: 10, width: '100%' }}>
                            <Paragraph style={{ marginBottom: 8, color: token.colorText }}>
                              {item.title === 'User registered'
                                ? `${item.user?.first_name} ${item.user?.second_name} from ${
                                    item.user?.additionalData?.company_name
                                  } has submitted a registration request on ${moment(
                                    item.createdAt,
                                  ).format('YYYY-MM-DD HH:mm')} Please review to take action.`
                                : item.description}
                            </Paragraph>

                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'end',
                                alignItems: 'center',
                                marginTop: 8,
                              }}
                            >
                              <Button
                                type="default"
                                size="small"
                                onClick={() => handleNotificationClick(item.id)}
                              >
                                Review
                              </Button>
                            </div>
                          </div>
                        )}
                      </List.Item>
                    );
                  }}
                />
              </div>
            );
          })
        )}
      </Spin>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      placement="bottomRight"
      open={visible}
      onOpenChange={setVisible}
    >
      <Badge id="notice-center" size="small" count={unreadCount}>
        <NoticeSvg style={{ cursor: 'pointer', color: color }} />
      </Badge>
    </Popover>
  );
};

export default AdminNotification;
